import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import DocSection, {
  DocSubsection,
  DocNote,
  DocImage,
} from "../components/DocSection";

const Analytics = () => {
  const { t } = useTranslation();

  return (
    <DocSection id="analytics" title={t("docs.analytics.title")}>
      {/* Recommendations Section */}
      <DocSubsection id="recommendations" title={t("docs.analytics.recommendations")}>
        <p>{t("docs.analytics.recommendationsDesc")}</p>

        <DocImage
          src="/docs/recommendations-page.png"
          alt="Recommendations and Analytics Page"
          caption={t("docs.images.recommendationsPage")}
        />

        <h4>{t("docs.analytics.recommendationsWhat")}</h4>
        <p>{t("docs.analytics.recommendationsWhatDesc")}</p>

        <h4>{t("docs.analytics.recommendationsHow")}</h4>
        <p>{t("docs.analytics.recommendationsHowDesc")}</p>
        <ul>
          <li>{t("docs.analytics.recommendationsMethod1")}</li>
          <li>{t("docs.analytics.recommendationsMethod2")}</li>
          <li>{t("docs.analytics.recommendationsMethod3")}</li>
        </ul>

        <h4>{t("docs.analytics.recommendationsInterpret")}</h4>
        <p>{t("docs.analytics.recommendationsInterpretDesc")}</p>

        <Link to="/recommendations" className="doc-link">
          {t("docs.analytics.recommendationsLink")} →
        </Link>
      </DocSubsection>

      {/* Risk Scores Section */}
      <DocSubsection id="risk-scores" title={t("docs.analytics.riskScores")}>
        <p>{t("docs.analytics.riskScoresDesc")}</p>

        <DocImage
          src="/docs/risk-scores-map.png"
          alt="Risk Scores Geographic Map"
          caption={t("docs.images.riskScoresMap")}
        />

        <h4>{t("docs.analytics.avgRiskScoreTitle")}</h4>
        <p>{t("docs.analytics.avgRiskScoreDesc")}</p>

        <h4>{t("docs.analytics.riskCategories")}</h4>
        <ul>
          <li>
            <strong className="risk-very-low">{t("docs.analytics.riskVeryLow")}:</strong>{" "}
            {t("docs.analytics.riskVeryLowDesc")}
          </li>
          <li>
            <strong className="risk-low">{t("docs.analytics.riskLow")}:</strong>{" "}
            {t("docs.analytics.riskLowDesc")}
          </li>
          <li>
            <strong className="risk-mid">{t("docs.analytics.riskMid")}:</strong>{" "}
            {t("docs.analytics.riskMidDesc")}
          </li>
          <li>
            <strong className="risk-high">{t("docs.analytics.riskHigh")}:</strong>{" "}
            {t("docs.analytics.riskHighDesc")}
          </li>
          <li>
            <strong className="risk-very-high">{t("docs.analytics.riskVeryHigh")}:</strong>{" "}
            {t("docs.analytics.riskVeryHighDesc")}
          </li>
        </ul>

        <h4>{t("docs.analytics.riskMetrics")}</h4>
        <p>{t("docs.analytics.riskMetricsDesc")}</p>
        <ul>
          <li>
            <strong>avgRiskScore:</strong> {t("docs.analytics.metricRiskScore")}
          </li>
          <li>
            <strong>avgAge:</strong> {t("docs.analytics.metricAge")}
          </li>
          <li>
            <strong>avgBMI:</strong> {t("docs.analytics.metricBMI")}
          </li>
          <li>
            <strong>numPatients:</strong> {t("docs.analytics.metricPatients")}
          </li>
        </ul>

        <DocNote type="info">
          {t("docs.analytics.riskScoresNote")}
        </DocNote>
      </DocSubsection>

      {/* Correlation Analysis Section */}
      <DocSubsection id="correlation" title={t("docs.analytics.correlation")}>
        <p>{t("docs.analytics.correlationDesc")}</p>

        <DocImage
          src="/docs/correlation-analysis.png"
          alt="Correlation Analysis Visualization"
          caption={t("docs.images.correlationAnalysis")}
        />

        <h4>{t("docs.analytics.correlationPurpose")}</h4>
        <p>{t("docs.analytics.correlationPurposeDesc")}</p>

        <h4>{t("docs.analytics.correlationInterpret")}</h4>
        <p>{t("docs.analytics.correlationInterpretDesc")}</p>
        <ul>
          <li>
            <strong>{t("docs.analytics.correlationPositive")}:</strong>{" "}
            {t("docs.analytics.correlationPositiveDesc")}
          </li>
          <li>
            <strong>{t("docs.analytics.correlationNegative")}:</strong>{" "}
            {t("docs.analytics.correlationNegativeDesc")}
          </li>
          <li>
            <strong>{t("docs.analytics.correlationNone")}:</strong>{" "}
            {t("docs.analytics.correlationNoneDesc")}
          </li>
        </ul>

        <DocNote type="warning">
          {t("docs.analytics.correlationWarning")}
        </DocNote>
      </DocSubsection>

      {/* Fusion Data Section */}
      <DocSubsection id="fusion" title={t("docs.analytics.fusion")}>
        <p>{t("docs.analytics.fusionDesc")}</p>

        <DocImage
          src="/docs/fusion-cluster-map.png"
          alt="Fusion Data Cluster Map"
          caption={t("docs.images.fusionClusterMap")}
        />

        <h4>{t("docs.analytics.fusionWhat")}</h4>
        <p>{t("docs.analytics.fusionWhatDesc")}</p>

        <h4>{t("docs.analytics.fusionClusters")}</h4>
        <p>{t("docs.analytics.fusionClustersDesc")}</p>
        <ul>
          <li>
            <strong>HH (High-High):</strong> {t("docs.analytics.clusterHH")}
          </li>
          <li>
            <strong>HL (High-Low):</strong> {t("docs.analytics.clusterHL")}
          </li>
          <li>
            <strong>LH (Low-High):</strong> {t("docs.analytics.clusterLH")}
          </li>
          <li>
            <strong>LL (Low-Low):</strong> {t("docs.analytics.clusterLL")}
          </li>
          <li>
            <strong>{t("docs.analytics.clusterNS")}:</strong>{" "}
            {t("docs.analytics.clusterNSDesc")}
          </li>
        </ul>

        <h4>{t("docs.analytics.fusionUsage")}</h4>
        <p>{t("docs.analytics.fusionUsageDesc")}</p>
      </DocSubsection>
    </DocSection>
  );
};

export default Analytics;
